import { useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Calendar,
  MapPin,
  UserCheck,
  CreditCard,
  BarChart3,
  Building2,
  Moon,
  Sun,
} from 'lucide-react';
import { NavLink, type NavLinkItem } from '@/components/molecules/NavLink';
import { useUserStore } from '@/store/userSlice';
import { useThemeStore } from '@/store/themeSlice';

// Отдельного раздела «Пользователи» нет: user — чистый логин, карточки
// логина живут на страницах исполнителя (/workers) и клиента (/clients)
const staffNavItems: NavLinkItem[] = [
  { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/templates', label: 'Шаблоны', icon: FileText },
  { href: '/visits', label: 'Визиты', icon: Calendar },
  { href: '/locations', label: 'Локации', icon: MapPin },
  { href: '/workers', label: 'Исполнители', icon: UserCheck },
  { href: '/clients', label: 'Клиенты', icon: Building2 },
  { href: '/payments', label: 'Оплаты', icon: CreditCard },
  { href: '/reports', label: 'Отчёты', icon: BarChart3 },
];

// Клиент видит только свои разделы; исполнители («Сотрудники») — в /workers,
// «Организация» — форма данных своей карточки (роль client)
const clientNavItems: NavLinkItem[] = [
  { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/profile', label: 'Организация', icon: Building2 },
  { href: '/templates', label: 'Шаблоны', icon: FileText },
  { href: '/visits', label: 'Визиты', icon: Calendar },
  { href: '/locations', label: 'Локации', icon: MapPin },
  { href: '/workers', label: 'Исполнители', icon: UserCheck },
  { href: '/reports', label: 'Отчёты', icon: BarChart3 },
];

export function Sidebar() {
  const { pathname } = useLocation();
  const user = useUserStore((s) => s.user);
  const isStaff = useUserStore((s) => s.isStaff);
  const logout = useUserStore((s) => s.logout);
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);

  const navItems = isStaff ? staffNavItems : clientNavItems;

  return (
    <aside className="sticky top-0 flex h-screen w-64 flex-col border-r border-[var(--border)] bg-surface p-4">
      <h1 className="text-lg font-bold mb-6 px-3 text-foreground">Visitor Check</h1>
      <nav className="flex flex-col gap-1 flex-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.href}
            item={item}
            isActive={pathname === item.href || pathname.startsWith(`${item.href}/`)}
          />
        ))}
      </nav>
      {user && (
        <div className="border-t border-[var(--border)] pt-3 mt-3 px-3">
          <p className="text-sm font-medium text-foreground truncate">{user.name ?? user.login}</p>
          {user.name && <p className="text-xs text-muted truncate mb-2">{user.login}</p>}
          <button
            type="button"
            onClick={logout}
            className="text-xs text-muted hover:text-foreground transition-colors"
          >
            Выйти
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={toggleTheme}
        className="mt-3 flex items-center gap-3 rounded-[var(--radius)] px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-surface-secondary"
      >
        {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
        {theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
      </button>
    </aside>
  );
}
